import React, { useState, memo, useRef, useEffect } from "react";
import { ComposableMap, Geographies, Geography, ZoomableGroup, useMapContext } from "react-simple-maps";
import { scaleLinear } from "d3-scale";
import { motion } from "framer-motion";
import { NUMERIC_TO_ISO3 } from "../utils/countryMapping";

const geoUrl = "https://unpkg.com/world-atlas@2.0.2/countries-110m.json";

const CountryShape = memo(({
  geo, countryData, fillColor, hasAvatar, avatarUrl,
  onClickGeo, onEnterGeo, onLeaveGeo, isMobile
}) => {
  const { path } = useMapContext();

  if (hasAvatar) {
    const bounds = path.bounds(geo);
    const bx = bounds[0][0];
    const by = bounds[0][1];
    const bw = bounds[1][0] - bx;
    const bh = bounds[1][1] - by;

    return (
      <g>
        <defs>
          <clipPath id={`clip-${geo.rsmKey}`}>
            <path d={geo.svgPath} />
          </clipPath>
        </defs>

        <Geography
          geography={geo}
          tabIndex={-1}
          style={{
            default: { fill: fillColor, stroke: "none", outline: "none" },
            hover:   { fill: fillColor, stroke: "none", outline: "none" }
          }}
        />

        <image
          href={avatarUrl}
          x={bx}
          y={by}
          width={bw}
          height={bh}
          preserveAspectRatio="xMidYMid slice"
          clipPath={`url(#clip-${geo.rsmKey})`}
          style={{ pointerEvents: "none", opacity: 0.8 }}
        />

        <Geography
          geography={geo}
          tabIndex={-1}
          onClick={(e) => onClickGeo(geo, e)}
          onMouseEnter={(e) => onEnterGeo(geo, e)}
          onMouseLeave={onLeaveGeo}
          style={{
            default: {
              fill: "transparent",
              stroke: "rgba(255,215,0,0.3)",
              strokeWidth: isMobile ? 0.3 : 0.45,
              outline: "none",
              transition: "all 0.15s ease"
            },
            hover: {
              fill: "transparent",
              stroke: "#FFD700",
              strokeWidth: isMobile ? 1.5 : 2.5,
              outline: "none",
              filter: "drop-shadow(0 0 12px rgba(255,215,0,0.8))"
            }
          }}
        />
      </g>
    );
  }

  return (
    <Geography
      geography={geo}
      tabIndex={-1}
      onClick={(e) => onClickGeo(geo, e)}
      onMouseEnter={(e) => onEnterGeo(geo, e)}
      onMouseLeave={onLeaveGeo}
      style={{
        default: {
          fill: fillColor,
          stroke: "rgba(255,215,0,0.3)",
          strokeWidth: isMobile ? 0.3 : 0.45,
          outline: "none",
          transition: "all 0.15s ease"
        },
        hover: {
          fill: fillColor,
          stroke: "#FFD700",
          strokeWidth: isMobile ? 1.5 : 2.5,
          outline: "none",
          filter: "drop-shadow(0 0 12px rgba(255,215,0,0.8))"
        }
      }}
    />
  );
});

const WorldMap = memo(({ countries = [], onCountryClick }) => {

  const safeCountries = countries || [];
  const [tooltipData, setTooltipData] = useState(null);
  const mapRef = useRef(null);

  const isMobile = window.innerWidth < 768;

  useEffect(() => {
    const svg = mapRef.current?.querySelector("svg");
    if (!svg) return;

    const nativeFocus = svg.focus;

    svg.focus = function () {
      nativeFocus.call(this, { preventScroll: true });
    };
  }, []);

  const maxBid = Math.max(
    ...safeCountries.map((c) => {
      const sorted = [...(c?.top_bidders || [])].sort(
        (a, b) => b.amount - a.amount
      );
      return sorted[0] ? Number(sorted[0].amount || 0) : 0;
    }),
    1
  );

  const colorScale = scaleLinear()
    .domain([0, maxBid * 0.3, maxBid * 0.6, maxBid])
    .range(["#1A1A1A", "#806921", "#D4AF37", "#FFD700"])
    .clamp(true);

 const getGeoCode = (geo) => {
  if (geo?.properties?.name === "Kosovo") {
    return "XK";
  }

  return (
    NUMERIC_TO_ISO3[geo.id] ||
    geo?.properties?.ISO_A2 ||
    geo?.properties?.iso_a2 ||
    geo?.properties?.ADM0_A3 ||
    geo?.id ||
    ""
  );
};

const getCountryData = (geo) => {
  const code = getGeoCode(geo);

  return safeCountries.find(
    (c) => c.country_code === code
  ) || null;
};

const handleCountryClick = (geo, event) => {
  event?.stopPropagation?.();

  const countryData = getCountryData(geo);

  if (onCountryClick) {
    onCountryClick(
      countryData || {
        country_code: getGeoCode(geo),
        country_name: geo.properties.name,
        top_bidders: []
      }
    );
  }
};

  const handleMouseEnter = (geo, event) => {
    const countryData = getCountryData(geo);

    const sorted = [...(countryData?.top_bidders || [])].sort(
      (a, b) => b.amount - a.amount
    );

    setTooltipData({
      name: countryData?.country_name || geo.properties.name,
      topBidders: sorted,
      x: event.clientX,
      y: event.clientY
    });
  };

  const handleMouseLeave = () => {
    setTooltipData(null);
  };

  const handleMouseMove = (event) => {
    if (!tooltipData) return;

    setTooltipData((prev) => ({
      ...prev,
      x: event.clientX,
      y: event.clientY
    }));
  };

  return (
    <div
      ref={mapRef}
      className="w-full h-[50vh] md:h-[600px] flex items-center justify-center bg-gradient-to-b from-black via-[#070707] to-[#111111] rounded-xl overflow-hidden"
      style={{ overflow: "hidden", outline: "none" }}
      onMouseMove={handleMouseMove}
    >

      <ComposableMap
        projection="geoMercator"
        projectionConfig={{
          scale: isMobile ? 140 : 170,
          center: [0, 0]
        }}
        style={{
          width: "100%",
          height: "100%",
          background: "#050505",
          outline: "none"
        }}
        tabIndex={-1}
      >

        <ZoomableGroup
          center={[0, 10]}
          zoom={1}
          onMoveEnd={() => {}}
          maxZoom={isMobile ? 18 : 12}
          minZoom={1}
          translateExtent={[
            [-1000, -300],
            [1000, 600]
          ]}
        >

          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => {

                const countryData = getCountryData(geo);

                const sorted = [...(countryData?.top_bidders || [])].sort(
                  (a, b) => b.amount - a.amount
                );

                const topBidder = sorted[0];

                const fillColor = topBidder
                  ? colorScale(Number(topBidder.amount || 0))
                  : "#1A1A1A";

                const avatarUrl = topBidder?.avatar_url;
                const hasAvatar = !!(avatarUrl && avatarUrl.trim());

                return (
                  <CountryShape
                    key={geo.rsmKey}
                    geo={geo}
                    countryData={countryData}
                    fillColor={fillColor}
                    hasAvatar={hasAvatar}
                    avatarUrl={avatarUrl}
                    onClickGeo={handleCountryClick}
                    onEnterGeo={handleMouseEnter}
                    onLeaveGeo={handleMouseLeave}
                    isMobile={isMobile}
                  />
                );

              })
            }
          </Geographies>

        </ZoomableGroup>

      </ComposableMap>

      {tooltipData && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="fixed pointer-events-none z-50 glass-panel-gold p-4 max-w-sm"
          style={{
            left: tooltipData.x + 16,
            top: tooltipData.y + 16
          }}
        >
          <div className="text-[#FFD700] font-bold text-lg font-['Cinzel'] mb-2">
            {tooltipData.name}
          </div>

          {(tooltipData.topBidders || []).length > 0 ? (
            <div className="space-y-1">
              {(tooltipData.topBidders || []).slice(0, 5).map((b, i) => (
                <div key={i} className="text-sm text-white flex justify-between gap-4 border-b border-[#333] pb-1">
                  <span className="truncate">
                    {i === 0 && "👑 "}
                    {b.user_name}
                  </span>
                  <span
                    className="font-bold"
                    style={{
                      color: "#ff0000",
                      textShadow: "0 0 4px black"
                    }}
                  >
                    €{Number(b.amount || 0).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-gray-300 text-sm">
              Be The First Owner
            </div>
          )}
        </motion.div>
      )}

    </div>
  );
});

export default WorldMap;
